import { Button, CircularProgress, Snackbar, Tab, Tabs } from "@material-ui/core";
import Axios from "axios";
import React from "react";
import { ArcGisMap, DataTable } from "../../../components";
import { configContext } from "../../../components/config-provider";
import { ArcGisSearch } from "../../../components/map/search";
import { TYPE } from "../../../types";
import { buildExportName } from "../../../utils";
import { downloadFile } from "../../../utils/download";
import { downloadExcel, stampExportName } from "../../../utils/excel-download";

interface InventoryProps {
  type: TYPE;
}

interface State {
  loading: boolean;
  data: any[];
  no: string;
  lampno: string;
}

// Tab label -> filename abbreviation, per the download naming convention
// (INV / FAULT / ANNO / MAINS). Any label not listed falls back to its
// uppercased form (e.g. "Lantern" -> "LANTERN").
const abbrev: Record<string, string> = {
  Inventory: "INV",
  Fault: "FAULT",
  Annotation: "ANNO",
  Maintenance: "MAINS",
};

const titles: Record<TYPE, string[]> = {
  lamppost: ["Inventory", "Fault", "Annotation", "Maintenance", "Lantern", "Controller", "Cable", "Connector"],
  lantern: ["Inventory", "Inspection", "Lamppost"],
  bollard: ["Inventory", "Fault", "Annotation", "Maintenance", "Cable", "Connector"],
  controller: ["Inventory", "Fault", "Annotation", "Maintenance", "Lamppost", "Cable", "Connector"],
  cable: ["Inventory", "Fault", "Maintenance", "Lamppost", "Controller"],
  gsds: ["Inventory", "Fault", "Lamps", "Annotation", "Maintenance"],
  connector: ["Inventory", "Lamppost", "Controller", "Drawpit"],
  drawpit: ["Inventory", "Connector"],
};

export const Inventory: React.FC<InventoryProps> = (props) => {
  const { type } = props;
  const { disableBollard } = React.useContext(configContext);

  const [state, setState] = React.useState<State>({
    data: [],
    loading: false,
    no: "",
    lampno: "",
  });

  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; }>({
    open: false,
    message: "",
  });

  const [activeTab, setActiveTab] = React.useState(0);

  const [exportingAll, setExportingAll] = React.useState<boolean>(false);

  // Keep one tab per category in fixed positional order (the API returns empty
  // arrays for categories with no records), so sub-tabs like Cable/Connector
  // stay visible even when empty. Hoisted out of the JSX below because the
  // Export All button sits *above* the tab bar and needs the same list.
  const tabs = React.useMemo(
    () =>
      state.data
        .map((rows, index) => ({
          label: titles[type][index] || `#${index}`,
          rows: Array.isArray(rows) ? rows : [],
          index,
        }))
        .filter((tab) => !(disableBollard && tab.label === "Bollard")),
    [state.data, type, disableBollard]
  );

  // The first category is the primary record. If it's empty the searched item
  // doesn't exist, so there are no tabs and nothing to export.
  const primaryRows = state.data[0];
  const hasRecords =
    tabs.length > 0 && Array.isArray(primaryRows) && primaryRows.length > 0;

  const handleOnClick = (no: string) => {
    setState({ ...state, loading: true, no });
    setActiveTab(0);
    Axios.get(`/${type}`, { params: { no } }).then((res) => {
      const { data } = res;
      setState({
        ...state,
        data,
        loading: false,
        no,
        lampno: no,
      });
    });
  };

  const handleOnClickDownloadButton = (no: string) => {
    setState((prev) => ({ ...prev, loading: true }));
    Axios.get(`/${type}/image`, { params: { no }})
      .then((res) => {
        downloadFile(no + ".pdf", new Blob([new Uint8Array(res.data.data)], { type: "application/pdf" }));
      })
      .catch(() => {
        setSnackbar({ open: true, message: "No PDF found" });
      })
      .finally(() => {
        setState((prev) => ({ ...prev, loading: false }));
      });
  };

  const handleOnClickExportAllButton = () => {
    // One request already loaded every tab, so this is purely a packaging
    // step: one worksheet per tab, in tab order. Tabs with no records are
    // skipped — their columns are derived from the first row, so there would
    // be no header row to write.
    const sheets = tabs
      .filter((tab) => tab.rows.length > 0)
      .map((tab) => ({
        name: tab.label,
        columns: Object.keys(tab.rows[0]).map((key) => ({
          title: key,
          dataIndex: key,
        })),
        data: tab.rows,
      }));

    if (sheets.length === 0) return;

    setExportingAll(true);

    downloadExcel({
      filename: stampExportName(buildExportName(type, "ALL", state.lampno)),
      sheets,
    })
      .catch(() => {
        setSnackbar({ open: true, message: "Export failed" });
      })
      .finally(() => {
        setExportingAll(false);
      });
  };

  return (
    <>
      <div className="map">
        <ArcGisMap type={type} />
        <ArcGisSearch
          type={type}
          value={state.no}
          onChange={(value) => {
            setState({
              ...state,
              no: value,
            });
          }}
          onClick={handleOnClick}
        />
      </div>
      <div className="inventory lamppost">
        <h1>
          {state.lampno}
        </h1>
        {state.lampno && (type === "lamppost" || hasRecords) ? (
          <div className="inventory-actions">
            {type === "lamppost" && (
              <Button
                variant="contained"
                onClick={() => handleOnClickDownloadButton(state.lampno)}
                disabled={state.loading}
              >
                Download image report
                {
                  state.loading && (
                    <CircularProgress
                      size={24}
                      style={{
                        color: 'green',
                        textAlign: 'center',
                        position: 'absolute'
                      }}
                    />
                  )
                }
              </Button>
            )}

            {/* `state.loading` disables this as well as `exportingAll`: a
                search in flight leaves the *previous* asset's rows in
                state.data, which would export under the new asset's name. */}
            {hasRecords && (
              <Button
                variant="contained"
                onClick={handleOnClickExportAllButton}
                disabled={state.loading || exportingAll}
              >
                Export All
                {
                  exportingAll && (
                    <CircularProgress
                      size={24}
                      style={{
                        color: 'green',
                        textAlign: 'center',
                        position: 'absolute'
                      }}
                    />
                  )
                }
              </Button>
            )}
          </div>
        ) : <></>}
        {(() => {
          // No primary record -> the searched item doesn't exist, so show a
          // single page-level message (only once a search has run; keep the
          // untouched page blank).
          if (!hasRecords) {
            return state.lampno ? (
              <div className="inventory-no-record">No record found</div>
            ) : null;
          }

          const current = Math.min(activeTab, tabs.length - 1);
          const active = tabs[current];

          return (
            <>
              <Tabs
                className="inventory-tabs"
                value={current}
                onChange={(_evt, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                indicatorColor="primary"
                textColor="primary"
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.index}
                    label={`${tab.label} (${tab.rows.length})`}
                  />
                ))}
              </Tabs>
              {active.rows.length > 0 ? (
                <DataTable
                  title={active.label}
                  exportName={buildExportName(type, abbrev[active.label] || active.label, state.lampno)}
                  columns={Object.keys(active.rows[0]).map((key) => ({
                    title: key,
                    dataIndex: key,
                  }))}
                  data={active.rows}
                />
              ) : (
                <div className="inventory-no-record">No record found</div>
              )}
            </>
          );
        })()}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};
